import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiEye, FiArrowUp, FiArrowDown, FiMinus } from 'react-icons/fi';
import { subtleBadgeProps } from '@core/utils/subtleBadge';
import type {
  DashboardFinanceSnapshotMetrics,
  DashboardFinanceSnapshotPeriod,
} from '../types/dashboard';
import { financeSnapshotPath } from '../utils/dashboardPaths';

interface Props {
  periods: DashboardFinanceSnapshotPeriod[];
  scope: string;
  scopeId: number;
}

type MetricKind = 'money' | 'count' | 'countWithTrend';

type MetricKey = keyof Pick<
  DashboardFinanceSnapshotMetrics,
  'total_tithes' | 'total_general_offering' | 'membership_this_month' | 'membership_last_month'
>;

const METRIC_ROWS: ReadonlyArray<{ label: string; key: MetricKey; kind: MetricKind }> = [
  { label: 'Total Tithes', key: 'total_tithes', kind: 'money' },
  { label: 'Total General Offering', key: 'total_general_offering', kind: 'money' },
  { label: 'Membership This Month', key: 'membership_this_month', kind: 'countWithTrend' },
  { label: 'Membership Last Month', key: 'membership_last_month', kind: 'count' },
];

function formatMoney(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

function periodHasAnyData(metrics: DashboardFinanceSnapshotMetrics): boolean {
  return METRIC_ROWS.some(({ key }) => metrics[key] !== null);
}

function TrendIcon({ trend }: { trend: 'up' | 'down' | 'flat' }) {
  if (trend === 'up') {
    return <FiArrowUp className="dashboard-finance-trend dashboard-finance-trend--up" aria-hidden />;
  }
  if (trend === 'down') {
    return <FiArrowDown className="dashboard-finance-trend dashboard-finance-trend--down" aria-hidden />;
  }
  return <FiMinus className="dashboard-finance-trend dashboard-finance-trend--flat" aria-hidden />;
}

interface MetricValueProps {
  kind: MetricKind;
  raw: number | null;
  trend: DashboardFinanceSnapshotMetrics['membership_trend'];
}

const MetricValue: React.FC<MetricValueProps> = ({ kind, raw, trend }) => {
  if (raw === null) {
    return <span className="dashboard-finance-row__value dashboard-finance-row__value--empty">n/a</span>;
  }

  const display =
    kind === 'money' ? formatMoney(raw) : formatCount(raw);

  return (
    <span className="dashboard-finance-row__value">
      {display}
      {kind === 'countWithTrend' && trend && (
        <TrendIcon trend={trend} />
      )}
    </span>
  );
};

const FinanceSnapshot: React.FC<Props> = ({ periods, scope, scopeId }) => {
  if (!periods.length) return null;

  return (
    <Card className="ntcog-card dashboard-section-card mb-4">
      <Card.Header>
        <div>Finance snapshot</div>
        <small className="text-muted fw-normal">
          Submitted finance and membership data for open periods and the two most recently closed periods
        </small>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="dashboard-finance-list">
          {periods.map((period) => {
            const { metrics } = period;
            const hasAnyData = periodHasAnyData(metrics);

            return (
              <div key={period.report_tag_id} className="dashboard-finance-period">
                <div className="dashboard-finance-period__header d-flex align-items-center justify-content-between gap-3">
                  <div className="min-w-0 d-flex align-items-center gap-2 flex-wrap">
                    <div className="fw-semibold">{period.period_name}</div>
                    {period.status === 'closed' && (
                      <Badge {...subtleBadgeProps('secondary')}>Closed</Badge>
                    )}
                  </div>
                  {hasAnyData ? (
                    <Link
                      to={financeSnapshotPath(scope, scopeId, period.report_tag_id, period.department_id)}
                      className="dashboard-finance-period__link"
                      aria-label={`View finance report for ${period.period_name}`}
                      title={`View finance report for ${period.period_name}`}
                    >
                      <FiEye />
                    </Link>
                  ) : null}
                </div>

                {!hasAnyData ? (
                  <p className="dashboard-finance-period__empty mb-0" role="status">
                    No data available for this period.
                  </p>
                ) : (
                  <div className="dashboard-finance-ledger">
                    {METRIC_ROWS.map(({ label, key, kind }) => (
                      <div key={key} className="dashboard-finance-row">
                        <span className="dashboard-finance-row__label">{label}</span>
                        <MetricValue
                          kind={kind}
                          raw={metrics[key]}
                          trend={metrics.membership_trend}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default FinanceSnapshot;
